import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_DETAIL_STATE } from "./constants";
import { IDetailState } from "./interfaces";
import { CONFIG, IConfig } from "~/config";
import { store } from "~/state/store";
import { detail } from "~/util/providers/poiagg";
import { IPoi } from "~/interfaces";
import { detailComputeCenterZoom, postProcessPOI } from "~/util/detail";

let detailFRPCRequest = null;

function cancelDetailRequest() {
	if (detailFRPCRequest) {
		detailFRPCRequest.abort();
		detailFRPCRequest = null;
	}
}

/** spusti nacitani detailu dle source a id */
export const loadDetail = createAsyncThunk(
	"detail/load",
	async ({ source, id, mark = null, showZ = "", lang = CONFIG.langPriority }: Pick<IDetailState, "source" | "id"> & {
		mark?: IPoi["mark"],
		lang?: IConfig["langPriority"],
		showZ?: string;
	}) => {
		// pokud se zrovna nacita detail, tak toho nechame
		cancelDetailRequest();

		const data = {
			fetchPhoto: true,
			ratios: ["default", "1x1", "16x9"],
			wikimedia: true,
			lang,
			...source === "firm"
				? {
					query: store.getState().search.query,
				}
				: {},
		};

		const detailResponse = detail(source, id, data);

		detailFRPCRequest = detailResponse.request;
		const response = await detailResponse.promise;

		if (response.status !== 200) {
			return {
				data: null,
				mark: null,
				status: response.status,
			};
		}

		const poi: IPoi = response.poi;

		postProcessPOI(poi);

		return {
			data: poi,
			mark: mark || poi.mark,
			centerZoom: detailComputeCenterZoom({
				poi,
				showZ,
			}),
			status: response.status,
		};
	},
);

const detailSlice = createSlice({
	name: "detail",
	initialState: DEFAULT_DETAIL_STATE,
	reducers: {
		/** nastavi data detailu primo z objektu, misto delani dotazu na server */
		setDetail: (state: IDetailState, action: PayloadAction<Pick<IDetailState, "data" | "mark">>) => {
			state.source = action.payload.data.source;
			state.id = action.payload.data.id;
			state.loading = false;
			state.status = 200;
			state.data = action.payload.data;
			state.mark = action.payload.mark ? { ...action.payload.mark } : { ...action.payload.data.mark };
		},
		/** zrusi veskera data o nactenem detailu (tedy de facto zavre detail) */
		unsetDetail: (state: IDetailState) => {
			state.source = DEFAULT_DETAIL_STATE.source;
			state.id = DEFAULT_DETAIL_STATE.id;
			state.loading = DEFAULT_DETAIL_STATE.loading;
			state.status = DEFAULT_DETAIL_STATE.status;
			state.data = DEFAULT_DETAIL_STATE.data;
			state.mark = DEFAULT_DETAIL_STATE.mark;
			state.centerZoom = DEFAULT_DETAIL_STATE.centerZoom;
		},
	},
	extraReducers(builder) {
		builder.addCase(loadDetail.pending, (state, action) => {
			state.source = action.meta.arg.source;
			state.id = action.meta.arg.id;
			state.loading = true;
			state.status = DEFAULT_DETAIL_STATE.status;
			state.data = DEFAULT_DETAIL_STATE.data;
			state.mark = action.meta.arg.mark || DEFAULT_DETAIL_STATE.mark;
			state.centerZoom = DEFAULT_DETAIL_STATE.centerZoom;
		});
		builder.addCase(loadDetail.fulfilled, (state, action) => {
			const { status, data, mark, centerZoom } = action.payload;
			const id = data.id;

			state.loading = false;
			state.status = status;
			state.data = {
				...data,
				id,
			};
			state.mark = mark;
			state.centerZoom = centerZoom;

			// prepiseme podle dat
			state.source = data.source;
			state.id = id;
		});
		builder.addCase(loadDetail.rejected, state => {
			state.loading = false;
			state.status = 500;
			state.data = DEFAULT_DETAIL_STATE.data;
			state.mark = DEFAULT_DETAIL_STATE.mark;
		});
	},
});

// dle "duck" metodiky jsou akce pojmenovane exporty a reducer default export
export const { setDetail, unsetDetail } = detailSlice.actions;
export default detailSlice.reducer;
